/**
 * Limites et bornes de validation, partagees par le serveur et les clients.
 *
 * Le client s'en sert pour valider avant l'envoi et afficher un compteur de
 * caracteres ; le serveur s'en sert pour refuser. Les deux lisent la meme
 * source, donc les messages d'erreur ne peuvent pas divergent.
 *
 * IMPORTANT : le serveur reste la seule autorite. Une validation cote client est
 * un confort d'interface, jamais une protection -- un client est modifiable.
 */

export const LIMITS = {
  /** Corps d'un message de chat. */
  messageMaxLength: 4000,

  /** Champs du formulaire pre-chat. */
  visitorNameMaxLength: 80,
  visitorEmailMaxLength: 200,
  visitorPhoneMaxLength: 32,

  /**
   * Fiche client : l'identite tenue par l'equipe.
   *
   * Les notes sont larges (4000, comme un message) parce que c'est la ou
   * atterrit tout ce qui n'a pas de case : un numero de commande, le nom du
   * conjoint qui rappelle, la raison d'un geste commercial. Une limite etroite
   * ferait ecrire ailleurs -- c'est-a-dire nulle part.
   */
  visitorFirstNameMaxLength: 80,
  visitorLastNameMaxLength: 80,
  visitorCompanyMaxLength: 140,
  visitorNotesMaxLength: 4000,

  /** Formulaire hors horaires. */
  offlineMessageMinLength: 10,
  offlineMessageMaxLength: 4000,

  /** Suivi de navigation. */
  pageUrlMaxLength: 2000,
  pageTitleMaxLength: 300,
  referrerMaxLength: 2000,

  /** Reponses pre-enregistrees. */
  cannedShortcutMaxLength: 40,
  cannedBodyMaxLength: 4000,

  /**
   * Silence au bout duquel un visiteur passe en veille, en secondes.
   *
   * Bornes partagees par les quatre cotes -- le serveur qui valide, le widget
   * qui arme son minuteur, le loader qui replie sur le defaut face a un serveur
   * plus ancien, et le formulaire du back-office. Une seule source, sinon le
   * champ accepte au clavier ce que le serveur refuse ensuite.
   *
   * Le PLANCHER de 30 s protege de l'auto-sabotage : en dessous, un visiteur qui
   * lit un paragraphe bascule en veille puis en sort a chaque mouvement de
   * souris, et la liste de la console clignote au lieu d'informer. Le PLAFOND
   * d'une heure protege de l'autre extreme, un delai si long que plus personne
   * n'apparait jamais en veille -- ce qui revient a desactiver la
   * fonctionnalite sans le dire.
   */
  idleAfterSecondsMin: 30,
  idleAfterSecondsMax: 3600,
  idleAfterSecondsDefault: 180,

  /**
   * Poids maximal d'une piece jointe, en octets.
   *
   * 10 Mo : de quoi passer une capture d'ecran, un PDF de devis ou une photo de
   * telephone sans y penser, ce qui est l'usage reel. Au-dela, on entre dans le
   * transfert de fichiers, qui n'est pas ce que fait un tchat -- et chaque piece
   * pese ce poids dans la base ET dans chaque sauvegarde (cf. modele Attachment).
   *
   * CONTROLE PAR LE SERVEUR, TOUJOURS. Le client verifie avant l'envoi pour dire
   * « trop lourd » sans faire attendre, mais c'est le parseur de corps qui
   * refuse -- un client est modifiable.
   */
  attachmentMaxBytes: 10 * 1024 * 1024,

  /**
   * Longueur du nom de fichier conserve.
   *
   * Un nom n'est jamais utilise pour composer un chemin -- les octets vivent en
   * base, il n'y a pas de disque -- mais il est reaffiche, et un nom de deux
   * mille caracteres casserait la mise en page d'un fil de conversation.
   */
  attachmentFilenameMaxLength: 200,

  /**
   * -------------------------------------------------------------------------
   * IDENTITE TRANSMISE PAR LA BOUTIQUE (module PrestaShop)
   * -------------------------------------------------------------------------
   *
   * Une boutique qui connait son client connecte nous le dit, et le dit SIGNE :
   * un jeton `ci1.<charge>.<signature>` scelle avec le secret du site. Les
   * bornes ci-dessous s'appliquent APRES verification de la signature -- une
   * signature valide prouve la provenance, elle ne prouve pas que la boutique
   * envoie des valeurs raisonnables.
   */

  /**
   * Poids maximal du jeton entier, en caracteres.
   *
   * Le jeton voyage dans un attribut HTML sur chaque page de la boutique : il
   * doit rester petit. 4 Ko laissent largement la place a une identite et a
   * cinq commandes, et bornent ce que le serveur accepte de decoder avant meme
   * de verifier la signature.
   */
  shopTokenMaxLength: 4096,

  /**
   * Duree de validite maximale ACCEPTEE PAR LE SERVEUR, en secondes.
   *
   * La boutique fixe elle-meme l'expiration du jeton, mais elle ne peut pas
   * fixer celle-ci : sans plafond de notre cote, un module mal regle -- ou
   * modifie -- emettrait des jetons valables un an, et le vol d'une page
   * enregistree vaudrait usurpation permanente. Trente minutes couvrent
   * largement le temps qu'une page reste ouverte avant rechargement.
   */
  shopIdentityMaxAgeSeconds: 1800,

  /**
   * Tolerance d'horloge entre la boutique et nous, en secondes.
   *
   * Deux serveurs distincts, souvent chez deux hebergeurs : quelques secondes
   * de derive sont normales. Sans cette tolerance, un jeton parfaitement
   * legitime serait refuse comme « emis dans le futur ».
   */
  shopClockSkewSeconds: 120,

  /** Reference d'une commande. PrestaShop en emet de 9 caracteres. */
  orderReferenceMaxLength: 32,
  /** Libelle d'etat et montant, tels que la boutique les affiche. */
  orderLabelMaxLength: 60,
  /** Identifiant client chez la boutique, recopie tel quel. */
  shopCustomerRefMaxLength: 64,

  /**
   * Nombre de commandes proposees au visiteur.
   *
   * Cinq, et pas la liste entiere : la question posee est « votre demande
   * concerne-t-elle une commande ? », pas « parcourez votre historique ». Une
   * liste longue transforme une question d'une seconde en formulaire, et fait
   * grossir le jeton present sur chaque page.
   */
  shopOrdersMax: 5,

  /**
   * Secret partage entre la boutique et le serveur de chat.
   *
   * 32 octets tires au sort, presentes en base64url -- soit 43 caracteres. Le
   * plancher de 32 caracteres protege contre une valeur saisie a la main.
   */
  shopSecretMinLength: 32,
  shopSecretMaxLength: 200,

  /** Configuration de site. */
  siteNameMaxLength: 120,
  allowedDomainsMax: 50,
  privacyPolicyUrlMaxLength: 2000,

  /** Pagination des listes du back-office. */
  pageSizeDefault: 25,
  pageSizeMax: 100,

  /**
   * Recul maximal du panneau de statistiques, en semaines.
   *
   * DEUX ROLES, ET C'EST POURQUOI LA CONSTANTE EST PARTAGEE. Le serveur refuse
   * une semaine plus ancienne (elle borne la fenetre interrogeable, a la place
   * d'un quota de debit qui n'aurait pas de sens sur un agregat anonyme) ; la
   * console desactive le bouton « Semaine precedente » avec la MEME valeur. Deux
   * constantes distinctes donneraient un bouton actif qui repond 400.
   *
   * 26 semaines tiennent largement dans les 365 jours de conservation par
   * defaut : au-dela, on interrogerait surtout des journees deja purgees.
   */
  statsMaxWeeksBack: 26,

  /**
   * Conservation du compteur quotidien de visiteurs, en semaines.
   *
   * HUIT, ET C'EST L'EXPLOITANT QUI L'A FIXE : « se garder que les 8 dernieres
   * semaines pas plus ». C'est la seule donnee que ce panneau fait ECRIRE, et
   * une duree courte est la contrepartie de cette ecriture.
   *
   * CONSEQUENCE A NE PAS MASQUER : au-dela, la colonne « visiteurs vus » n'a
   * plus rien a montrer, et le panneau doit y afficher un tiret -- jamais un
   * zero, qui affirmerait qu'il n'est venu personne. Les trois autres chiffres,
   * eux, se recalculent depuis les donnees vivantes et remontent a
   * `statsMaxWeeksBack`.
   */
  statsDailyRetentionWeeks: 8,
} as const;

/**
 * Quotas de debit, par fenetre glissante d'une minute.
 *
 * Volontairement genereux pour un usage normal et bas pour un usage abusif :
 * personne n'envoie 20 messages en une minute a la main, mais un script si.
 */
export const RATE_LIMITS = {
  visitorMessagesPerMinute: 20,
  visitorPageViewsPerMinute: 60,
  /**
   * Identifications d'un visiteur INCONNU, par IP.
   *
   * Ce compteur borne la CREATION de visiteurs en masse. Il reste bas : creer
   * dix enregistrements par minute depuis une meme adresse est deja genereux
   * pour un usage honnete.
   *
   * Il sert aussi `visitor:details` et `visitor:consent`, qui sont des ecritures
   * declenchees par un geste humain -- d'ou l'importance de NE PAS le relever
   * pour resoudre un probleme de frequence sur un autre chemin.
   */
  visitorIdentifyPerMinute: 10,
  /**
   * Rattachements d'un visiteur DEJA CONNU, par visiteur.
   *
   * Chemin distinct, et c'est tout l'objet de cette entree. Depuis que le widget
   * se raccorde des le chargement de chaque page, un visiteur qui navigue
   * s'identifie plusieurs fois par minute : le compteur de CREATION, pense pour
   * un geste rare et compte par IP, refusait alors des visiteurs parfaitement
   * legitimes -- et tous ceux qui partageaient leur adresse.
   *
   * Calibre comme les pages vues (60/minute), puisque c'est le meme rythme : une
   * identification par page. Compte PAR VISITEUR, donc insensible au NAT.
   */
  visitorReattachPerMinute: 60,

  /**
   * Depots de pieces jointes, par visiteur et par minute.
   *
   * BAS, ET DELIBEREMENT. C'est la seule action par laquelle un visiteur fait
   * ecrire DIX MEGAOCTETS dans la base d'un coup. Sans plafond, une page publique
   * devient un espace de stockage gratuit, et la sauvegarde nocturne du client
   * grossit de plusieurs gigaoctets pendant la nuit ou personne ne regarde.
   * Cinq envois par minute couvrent largement quelqu'un qui joint trois captures
   * a la suite.
   */
  visitorAttachmentsPerMinute: 5,

  /**
   * Depots de pieces jointes, par agent et par minute.
   *
   * Plus large : un agent est authentifie, identifie, et son poste appartient a
   * l'exploitant. La borne existe pour attraper une boucle, pas un abus.
   */
  agentAttachmentsPerMinute: 30,

  /**
   * Presentations d'un jeton d'identite boutique, par visiteur et par minute.
   *
   * Calibre comme le rattachement (60/minute) : la boutique remet son jeton a
   * chaque page vue, exactement au meme rythme que l'identification. Le
   * compteur est la pour attraper une boucle, pas pour rationner un usage
   * normal -- et une verification de signature ne touche ni la base ni Redis.
   */
  shopIdentityPerMinute: 60,

  offlineFormPerHour: 5,
  agentMessagesPerMinute: 120,
  loginAttemptsPerQuarterHour: 20,
  /**
   * Telechargements de l'application agents, par IP et par heure.
   *
   * Cette route est publique -- un agent doit pouvoir installer la console avant
   * d'avoir un compte -- et sert une centaine de megaoctets. Sans plafond, elle
   * ferait de ce serveur un relais de bande passante gratuit. Dix reprises dans
   * l'heure couvrent largement une installation qui echoue et qu'on recommence.
   */
  appDownloadsPerHour: 10,

  /**
   * Emails de test envoyes depuis le back-office, par administrateur et par
   * heure.
   *
   * POURQUOI BORNER UNE FONCTION RESERVEE AUX ADMINISTRATEURS. Le bouton
   * « tester » fait partir un vrai email, vers une adresse librement saisie, par
   * le serveur d'envoi d'un client. Sans limite, c'est un relais de courrier
   * commande par une seule requete HTTP : une session d'administration volee, ou
   * un script laisse en boucle, suffirait a faire classer le domaine du client
   * comme source d'indesirables. Reparer cette reputation prend des semaines.
   *
   * Vingt essais par heure couvrent trois sites qu'on configure de bout en bout
   * dans la meme session, en se trompant plusieurs fois.
   */
  mailTestsPerHour: 20,
} as const;

/** Durees de vie, en secondes. */
export const TTL = {
  /** Presence d'un visiteur dans Redis : au-dela, il est considere parti. */
  visitorPresenceSeconds: 60,
  /** Presence d'un agent dans Redis. */
  agentPresenceSeconds: 90,
  /** Session agent (jeton d'acces). */
  agentSessionSeconds: 7 * 24 * 60 * 60,
} as const;

/**
 * Motifs de validation partages.
 *
 * Le controle de l'email reste volontairement permissif : le but est d'attraper
 * les fautes de saisie, pas de rejeter des adresses valides mais exotiques.
 */
export const PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  time: /^([01]\d|2[0-3]):[0-5]\d$/,
  hexColor: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
  /** Domaine ou sous-domaine, sans schema ni chemin. Accepte le joker de tete. */
  domain: /^(\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i,
} as const;
